import { useCallback, useMemo } from 'react';
import { QUICK_ACCESS_TARGETS } from './useQuickAccessConfig';

interface PageUsageData {
  [path: string]: number;
}

const STORAGE_KEY = 'dashboard-page-usage';

function loadUsage(): PageUsageData {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) {
    console.error('Failed to load page usage data:', e);
  }
  return {};
}

function saveUsage(data: PageUsageData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('Failed to save page usage data:', e);
  }
}

export function trackPageVisit(path: string): void {
  // Ignore root dashboard
  if (path === '/' || path === '') return;
  // Normalize: remove query params for tracking
  const normalized = path.split('?')[0];
  const usage = loadUsage();
  usage[normalized] = (usage[normalized] || 0) + 1;
  saveUsage(usage);
}

export interface TopPage {
  path: string;
  count: number;
  labelKey: string;
  iconName: string;
}

export function usePageUsageTracker() {
  const getTopPages = useCallback((n: number, excludeIds?: string[]): TopPage[] => {
    const usage = loadUsage();
    const excludePaths = (excludeIds || [])
      .map(id => QUICK_ACCESS_TARGETS.find(t => t.id === id)?.path.split('?')[0])
      .filter((p): p is string => p !== undefined);

    return Object.entries(usage)
      .filter(([path]) => !excludePaths.includes(path))
      .sort(([, a], [, b]) => b - a)
      .slice(0, n)
      .map(([path, count]) => {
        // Try to match against known targets
        const target = QUICK_ACCESS_TARGETS.find(t => t.path.split('?')[0] === path);
        return {
          path,
          count,
          labelKey: target?.labelKey || `nav.${path.split('/').filter(Boolean)[0] || 'dashboard'}`,
          iconName: target?.iconName || 'ExternalLink',
        };
      });
  }, []);

  const topPages = useMemo(() => getTopPages(3), [getTopPages]);

  return {
    getTopPages,
    topPages,
    trackVisit: trackPageVisit,
  };
}
